import type { ColDef } from '@ag-grid-community/core';
import type { GridRow } from '../grid.types';

export function mapDataToGridRows<TType extends GridRow = GridRow>(data: Object[], columnDefinitions: ColDef[], primaryKey?: string | null): TType[] {
    return <TType[]>data.map((row: GridRow) => {
        for (let colDef of columnDefinitions || []) {
            row[colDef.colId ?? ''] = getCellValue(row, colDef);
        }

        return {
            ...row,
            _dirty: false,
            _uuid: row._uuid ?? getLegacyPrimaryKeyUuid(row, primaryKey) ?? crypto.randomUUID(),
        };
    });
}

export function getLegacyPrimaryKeyUuid(row: GridRow, primaryKey?: string | null): string | null {
    if (!primaryKey) return null;

    return getPrimaryKeyMapValue(row, primaryKey);
}

export function getCellValue(row: GridRow, colDef: ColDef) {
    if (!colDef.colId) return null;

    switch (colDef.cellDataType) {
        case 'boolean':
            return ['1', 1, true].includes(row[colDef.colId]);
        case 'number':
        case 'formatNumber':
            if (row[colDef.colId] || row[colDef.colId] === 0) {
                if (!isNaN(Number(row[colDef.colId]))) {
                    return parseFloat(row[colDef.colId]);
                }
                return row[colDef.colId];
            } else {
                return null;
            }
        case 'date':
            const dateString = row[colDef.colId];

            if (dateString && typeof dateString === 'string') {
                const dateParts = dateString.split('-');

                return dateParts.length === 3 ? new Date(parseInt(dateParts[0]), parseInt(dateParts[1]) - 1, parseInt(dateParts[2])) : null;
            } else if (dateString && dateString instanceof Date && dateString.toString() !== 'Invalid Date') {
                return dateString;
            } else {
                return null;
            }
        case 'datetime':
            const dateTimeString = row[colDef.colId];

            if (dateTimeString && typeof dateTimeString === 'string') {
                const dateParts = dateTimeString.split(' ')[0].split('T')[0].split('-');
                let timeString = [0, 0, 0];

                if (dateTimeString.includes(' ')) {
                    timeString = dateTimeString
                        .split(' ')[1]
                        .substring(0, 8)
                        .split(':')
                        .map((part) => +part);
                } else if (dateTimeString.includes('T')) {
                    timeString = dateTimeString
                        .split('T')[1]
                        .substring(0, 8)
                        .split(':')
                        .map((part) => +part);
                }

                let newDate = new Date();

                if (dateParts.length === 3) {
                    newDate.setFullYear(+dateParts[0]);
                    newDate.setMonth(+dateParts[1] - 1);
                    newDate.setDate(+dateParts[2]);
                    newDate.setHours(timeString[0]);
                    newDate.setMinutes(timeString[1]);
                    newDate.setSeconds(timeString[2]);
                    newDate.setMilliseconds(0);

                    return newDate;
                } else {
                    return null;
                }
            } else if (dateTimeString && dateTimeString instanceof Date && dateTimeString.toString() !== 'Invalid Date') {
                return dateTimeString;
            } else {
                return null;
            }
        default:
            return row[colDef.colId];
    }
}

export function getPrimaryKeyMapValue(row: GridRow, primaryKey: string): string | null {
    const value = row[primaryKey];

    if (value === null || value === undefined || value === '') return null;

    return `${value}`;
}

export function isGeneratedGridUuid(value?: string | null): boolean {
    return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

export function getRowUuidsByPrimaryKey<TType extends GridRow = GridRow>(rowData: TType[], primaryKey: string): Map<string, string> {
    const rowUuidsByPrimaryKey = new Map<string, string>();

    for (const row of rowData) {
        const primaryKeyValue = getPrimaryKeyMapValue(row, primaryKey);

        if (primaryKeyValue !== null && row._uuid) {
            rowUuidsByPrimaryKey.set(primaryKeyValue, row._uuid);
        }
    }

    return rowUuidsByPrimaryKey;
}

export function getRowsWithoutPrimaryKey<TType extends GridRow = GridRow>(rowData: TType[], primaryKey: string): TType[] {
    return rowData.filter((row) => getPrimaryKeyMapValue(row, primaryKey) === null);
}

export function preservePostSaveRowUuids<TType extends GridRow = GridRow>(rowData: TType[], primaryKey: string, rowUuidsByPrimaryKey: Map<string, string>, existingRows: TType[], existingRowsWithoutPrimaryKey: TType[], mapDataToGridRows: (rows: TType[]) => TType[]): TType[] {
    let rows = rowData ?? [];

    if (rows.some((row) => Object.hasOwn(row, '_uuid') === false)) {
        rows = mapDataToGridRows(rows);
    }

    const returnedPrimaryKeyValues = new Set(rows.map((row) => getPrimaryKeyMapValue(row, primaryKey)).filter((primaryKeyValue): primaryKeyValue is string => primaryKeyValue !== null));

    const rowsWithPreservedUuids = rows.map((row) => {
        const primaryKeyValue = getPrimaryKeyMapValue(row, primaryKey);
        const existingUuid = primaryKeyValue !== null ? rowUuidsByPrimaryKey.get(primaryKeyValue) : null;

        if (!existingUuid) return row;

        return { ...row, _uuid: existingUuid };
    });

    const rowsByUuid = new Map(rowsWithPreservedUuids.filter((row) => !!row._uuid).map((row) => [row._uuid!, row]));
    const usedRowUuids = new Set<string>();

    const rowsInExistingOrder = existingRows.flatMap((existingRow) => {
        const primaryKeyValue = getPrimaryKeyMapValue(existingRow, primaryKey);

        if (!existingRow._uuid) {
            return shouldKeepExistingPostSaveRow(primaryKeyValue, existingRow._uuid, returnedPrimaryKeyValues) ? [existingRow] : [];
        }

        const updatedRow = rowsByUuid.get(existingRow._uuid);

        if (!updatedRow) {
            return shouldKeepExistingPostSaveRow(primaryKeyValue, existingRow._uuid, returnedPrimaryKeyValues) ? [existingRow] : [];
        }

        usedRowUuids.add(existingRow._uuid);
        return [updatedRow];
    });

    const rowUuids = new Set(rowsWithPreservedUuids.map((row) => row._uuid).filter((uuid) => !!uuid));
    // const rowsStillBeingCreated = existingRowsWithoutPrimaryKey.filter((row) => !row._uuid || !rowUuids.has(row._uuid));
    // return [...rowsWithPreservedUuids, ...rowsStillBeingCreated];

    // const existingRowsToKeep = existingRows.filter((row) => !row._uuid || !rowUuids.has(row._uuid));
    // return [...rowsWithPreservedUuids, ...existingRowsToKeep];

    const newRows = rowsWithPreservedUuids.filter((row) => !row._uuid || !usedRowUuids.has(row._uuid));

    return [...rowsInExistingOrder, ...newRows];
}

function shouldKeepExistingPostSaveRow(primaryKeyValue: string | null, rowUuid: string | null | undefined, returnedPrimaryKeyValues: Set<string>): boolean {
    return primaryKeyValue === null || isGeneratedGridUuid(rowUuid) || returnedPrimaryKeyValues.has(primaryKeyValue);
}
